import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface BannerState {
  tone: 'warn' | 'error';
  title: string;
  body: string;
  dismissible: boolean;
  key: string;
}

export const SubscriptionBanner: React.FC = () => {
  const { partner } = useAuth();
  const navigate = useNavigate();
  const [dismissedKey, setDismissedKey] = useState<string | null>(
    () => sessionStorage.getItem('qp:sub-banner-dismissed')
  );
  const [override, setOverride] = useState<{ message: string } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { message: string };
      setOverride(detail);
    };
    window.addEventListener('subscription-expired', handler);
    return () => window.removeEventListener('subscription-expired', handler);
  }, []);

  // 402 override — критично, не dismissible
  if (override) {
    return (
      <BannerView
        tone="error"
        title="Подписка истекла"
        body={override.message || 'Заведение скрыто от гостей. Оплатите тариф, чтобы вернуть его в приложение и снова редактировать данные.'}
        dismissible={false}
        onClick={() => navigate('/billing')}
      />
    );
  }

  const sub = partner?.subscription;
  if (!sub) return null;

  let state: BannerState | null = null;
  const daysLeft = sub.days_left ?? 99;

  // Не активирован — на /  есть отдельная плашка-чеклист, тут полный баннер не нужен
  if (sub.is_activated === false) {
    return null;
  }

  if (!sub.is_active) {
    const abs = Math.abs(daysLeft);
    state = {
      tone: 'error',
      title: 'Подписка истекла',
      body:
        abs === 0
          ? 'Истекла сегодня — заведение скрыто от гостей. Оплатите тариф, и оно снова появится в приложении.'
          : `Истекла ${abs} дн. назад — заведение скрыто от гостей. Оплатите тариф, и оно снова появится в приложении.`,
      dismissible: false,
      key: `error:${sub.expires_at}`,
    };
  } else if (sub.is_on_trial && daysLeft <= 7) {
    state = {
      tone: 'warn',
      title: 'Пробный период заканчивается',
      body:
        daysLeft <= 0
          ? 'Триал завершается сегодня. Оплатите тариф, чтобы заведение не пропало из приложения.'
          : `Осталось ${daysLeft === 1 ? '1 день' : `${daysLeft} дн.`} на пробном. Оплатите подписку, чтобы продолжить.`,
      // Нельзя скрыть если 1 день и менее
      dismissible: daysLeft > 1,
      key: `warn:${sub.expires_at}:${daysLeft}`,
    };
  } else if (!sub.is_on_trial && daysLeft <= 3) {
    state = {
      tone: 'warn',
      title: 'Подписка скоро закончится',
      body: `Осталось ${daysLeft === 1 ? '1 день' : `${daysLeft} дн.`}. Продлите тариф, чтобы не было перерыва.`,
      dismissible: daysLeft > 1,
      key: `warn:paid:${sub.expires_at}:${daysLeft}`,
    };
  }

  if (!state) return null;
  if (state.dismissible && dismissedKey === state.key) return null;

  const handleDismiss = () => {
    sessionStorage.setItem('qp:sub-banner-dismissed', state!.key);
    setDismissedKey(state!.key);
  };

  return (
    <BannerView
      tone={state.tone}
      title={state.title}
      body={state.body}
      dismissible={state.dismissible}
      onDismiss={handleDismiss}
      onClick={() => navigate('/billing')}
    />
  );
};

interface BannerViewProps {
  tone: 'warn' | 'error';
  title: string;
  body: string;
  dismissible: boolean;
  onDismiss?: () => void;
  onClick: () => void;
}

const BannerView: React.FC<BannerViewProps> = ({ tone, title, body, dismissible, onDismiss, onClick }) => {
  const palette =
    tone === 'error'
      ? { bg: 'var(--md-error-container)', color: 'var(--md-on-error-container)', accent: 'var(--md-error)' }
      : {
          bg: 'var(--md-tertiary-container)',
          color: 'var(--md-on-tertiary-container)',
          accent: 'var(--md-tertiary)',
        };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        margin: '12px 16px 0',
        padding: '14px 16px',
        borderRadius: 'var(--md-shape-lg)',
        background: palette.bg,
        color: palette.color,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        boxShadow: 'var(--md-elevation-1)',
        cursor: 'pointer',
        transition: 'transform 150ms var(--md-ease-standard), box-shadow 150ms var(--md-ease-standard)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = 'var(--md-elevation-2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--md-elevation-1)';
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: 999,
          background: 'rgba(0,0,0,0.08)',
          color: palette.accent,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-hidden
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 2l10 19H2L12 2z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
          <path d="M12 9v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          <circle cx="12" cy="17.5" r="1" fill="currentColor" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</p>
        <p style={{ margin: '4px 0 10px', fontSize: 13, lineHeight: 1.45, opacity: 0.9 }}>{body}</p>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            fontWeight: 700,
            color: palette.accent,
          }}
        >
          Перейти к оплате
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
      {dismissible && onDismiss && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          aria-label="Скрыть уведомление"
          style={{
            appearance: 'none',
            background: 'rgba(0,0,0,0.06)',
            border: 'none',
            cursor: 'pointer',
            width: 32,
            height: 32,
            borderRadius: 999,
            color: palette.color,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            opacity: 0.7,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
};
